import { useCallback, useState } from 'react';
import Pagination from '../pages/Dashboard/components/Pagination';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '@constants/constants';

export interface PaginationState {
  pageNumber: number;
  pageSize: number;
  total: number;
}

interface UsePaginationProps {
  defaultPageNumber?: number;
  defaultPageSize?: number;

  onPageChange?: (pageNumber: number, pageSize: number) => void;
  onPageSizeChange?: (pageNumber: number, pageSize: number) => void;
  enablePageSize?: boolean;
}

interface PaginationComponentProps {
  fileName?: string;
  submoduleId?: string;
  chartId?: string;
  filterSelection?: Record<string, string>;
  disablePagination?: boolean;
}

export const usePaginationState = ({
  defaultPageNumber = DEFAULT_PAGE_NUMBER,
  defaultPageSize = DEFAULT_PAGE_SIZE,

  onPageChange,
  onPageSizeChange,

  enablePageSize = false,
}: UsePaginationProps = {}) => {
  const [pagination, setPagination] = useState<PaginationState>({
    pageNumber: defaultPageNumber,
    pageSize: defaultPageSize,
    total: 0,
  });

  const updatePagination = useCallback((value: Partial<PaginationState>) => {
    setPagination((prev) => ({
      ...prev,
      ...value,
    }));
  }, []);

  const handlePageChange = useCallback(
    (pageNumber: number) => {
      updatePagination({
        pageNumber,
      });
      onPageChange?.(pageNumber, pagination.pageSize);
    },
    [updatePagination, onPageChange],
  );

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      updatePagination({
        pageNumber: DEFAULT_PAGE_NUMBER,
        pageSize,
      });
      onPageSizeChange?.(DEFAULT_PAGE_NUMBER, pageSize);
    },
    [updatePagination, onPageSizeChange],
  );

  const PaginationComponent = ({
    fileName = '',
    submoduleId = '',
    chartId = '',
    filterSelection = {},
    disablePagination = false,
  }: PaginationComponentProps) => (
    <Pagination
      page={pagination.pageNumber}
      size={pagination.pageSize}
      total={pagination.total}
      handleChange={handlePageChange}
      setSize={enablePageSize ? handlePageSizeChange : undefined}
      fileName={fileName}
      submoduleId={submoduleId}
      chartId={chartId}
      filterSelection={filterSelection}
      disablePagination={disablePagination}
    />
  );

  return {
    pagination,
    updatePagination,
    PaginationComponent,
  };
};
